<template>
  <div class="scans-heatmap-widget">
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-else ref="heatmapContainer" class="heatmap-container">
      <!-- SVG will be rendered here by D3.js -->
    </div>

    <!-- Tooltip -->
    <div
      v-if="tooltip.visible"
      ref="tooltip"
      class="heatmap-tooltip"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
    >
      {{ tooltip.content }}
    </div>

    <!-- Scan Details Modal -->
    <v-dialog
      :value="modal.visible"
      :max-width="730"
      @keydown.esc="closeModal"
      @click:outside="closeModal"
    >
      <v-card class="scan-details-popup">
        <div class="scan-details-popup__action">
          <span class="close_btn" @click="closeModal">
            <span class="material-icons-outlined">close</span>
          </span>
        </div>

        <div class="scan-details-popup__title">
          {{ modal.date }}
        </div>
        <div class="scan-details-popup__subtitle">
          {{ modal.totalCount }} {{ modal.totalCount === 1 ? $t('dashboard.scans.details.scan') : $t('dashboard.scans.details.scans') }} {{ $t('dashboard.scans.details.scheduled') }}
        </div>

        <div class="scan-details-popup__body">
          <div class="scan-type-cards">
            <div
              v-for="group in modal.groupedScans"
              :key="group.scanType"
              class="scan-type-card"
              :class="{ 'scan-type-card--active': modal.hoveredType === group.scanType }"
              @mouseenter="modal.hoveredType = group.scanType"
            >
              <div class="scan-type-card__info">
                <div class="scan-type-card__name">{{ formatScanType(group.scanType) }}</div>
                <div class="scan-type-card__count">{{ group.count }}</div>
              </div>
            </div>
          </div>

          <div class="scan-details-popup__separator"></div>

          <div class="scan-urls-panel">
            <div class="scan-urls-panel__title">{{ $t('dashboard.scans.details.urls') }}</div>
            <div v-if="modal.hoveredType">
              <div
                v-for="(url, idx) in getUrlsForType(modal.hoveredType)"
                :key="idx"
                class="scan-urls-panel__item"
              >
                {{ url }}
              </div>
            </div>
            <div v-else class="scan-urls-panel__empty">
              {{ $t('dashboard.scans.details.urls.hover.hint') }}
            </div>
          </div>
        </div>

        <div class="scan-details-popup__chart">
          <div class="scan-details-popup__chart-title">{{ $t('dashboard.scans.details.daily.load.volume') }}</div>
          <div ref="loadVolumeChart" class="load-volume-container"></div>
        </div>

        <div class="scan-details-popup__buttons">
          <v-btn small depressed class="ok-button" @click="closeModal">
            {{ $t('dashboard.scans.details.ok') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as d3 from 'd3';
import moment from 'moment';
import { HEATMAP_GRID_LAYOUT, HEATMAP_DAY_LABELS, HEATMAP_3D_SETTINGS } from '@/constants/scansHeatmap';
import {
  createProjection3d,
  getBarFaces,
  getBoxCorners,
  getBounds,
  createFitTransform,
  toSvgPoints,
  getGroundTextTransform,
} from '@/utils/projection3dUtils';

export default {
  name: 'ScansHeatmapWidget',
  props: {
    heatmapData: {
      type: Object,
      required: true,
      validator(value) {
        return value && typeof value === 'object';
      },
    },
    dateRange: {
      type: Object,
      required: true,
      validator(value) {
        return value && value.start && value.end;
      },
    },
    width: {
      type: Number,
      default: 800,
    },
    height: {
      type: Number,
      default: 150,
    },
    is3dView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      error: null,
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        content: '',
      },
      modal: {
        visible: false,
        date: '',
        scans: [],
        groupedScans: [],
        totalCount: 0,
        hoveredType: null,
      },
    };
  },
  mounted() {
    this.validateAndRender();
  },
  watch: {
    heatmapData: {
      handler() {
        this.validateAndRender();
      },
      deep: true,
    },
    dateRange: {
      handler() {
        this.validateAndRender();
      },
      deep: true,
    },
    is3dView() {
      // The hovered cell is removed on re-render, so its mouseout never fires
      this.hideTooltip();
      this.validateAndRender();
    },
  },
  beforeDestroy() {
    // Clean up D3 elements
    if (this.$refs.heatmapContainer) {
      d3.select(this.$refs.heatmapContainer).selectAll('*').interrupt().remove();
    }
  },
  methods: {
    validateAndRender() {
      try {
        // Validate props
        if (!this.heatmapData || typeof this.heatmapData !== 'object') {
          throw new Error('Invalid heatmap data');
        }
        if (!this.dateRange || !this.dateRange.start || !this.dateRange.end) {
          throw new Error('Invalid date range');
        }

        this.renderHeatmap();
      } catch (err) {
        this.error = err.message || 'Failed to render heatmap';
        console.error('ScansHeatmapWidget error:', err);
      }
    },

    renderHeatmap() {
      const container = this.$refs.heatmapContainer;
      if (!container) return;

      // Clear any existing SVG
      d3.select(container).selectAll('*').interrupt().remove();

      // Reset error state on re-render
      this.error = null;

      const weeks = this.buildWeeks();
      const {
        cellSize, cellGap, daysInWeek, margin,
      } = HEATMAP_GRID_LAYOUT;

      // Calculate SVG dimensions based on content
      const svgWidth = margin.left + (weeks.length * (cellSize + cellGap)) + margin.right;
      const gridHeight = margin.top + (daysInWeek * (cellSize + cellGap)) + margin.bottom;
      // 3D also takes the free height below the grid; it keeps the grid's width so switching views can't resize the widget
      const svgHeight = this.is3dView ? Math.max(gridHeight, container.clientHeight) : gridHeight;

      // Create SVG
      const svg = d3.select(container)
        .append('svg')
        .attr('width', svgWidth)
        .attr('height', svgHeight);

      if (this.is3dView) {
        this.render3dGrid(svg, weeks, { width: svgWidth, height: svgHeight });
      } else {
        this.render2dGrid(svg, weeks);
      }
    },

    buildWeeks() {
      // Generate all dates in range
      const startDate = moment(this.dateRange.start);
      const endDate = moment(this.dateRange.end);
      const allDates = [];

      const currentDate = startDate.clone();
      while (currentDate.isSameOrBefore(endDate)) {
        allDates.push(currentDate.format('YYYY-MM-DD'));
        currentDate.add(1, 'day');
      }

      // Group dates by week (Sunday to Saturday columns)
      const weeks = [];
      let currentWeekIndex = -1;
      let lastWeekStart = null;

      allDates.forEach(dateStr => {
        const date = moment(dateStr);
        const dayOfWeek = date.day(); // 0 = Sunday, 6 = Saturday
        const weekStart = date.clone().startOf('week').format('YYYY-MM-DD');

        // Start a new week column when we hit a new week
        if (weekStart !== lastWeekStart) {
          currentWeekIndex += 1;
          weeks[currentWeekIndex] = [];
          lastWeekStart = weekStart;
        }

        weeks[currentWeekIndex].push({
          date: dateStr,
          dayOfWeek,
          count: this.heatmapData[dateStr]?.count || 0,
          scans: this.heatmapData[dateStr]?.scans || [],
        });
      });

      return weeks;
    },

    getMonthLabels(weeks) {
      const currentYear = moment().year();

      return weeks
        .map((week, weekIndex) => ({ weekIndex, date: moment(week[0].date) }))
        .filter(({ date }, index, items) => index === 0 || !date.isSame(items[index - 1].date, 'month'))
        .map(({ weekIndex, date }) => ({
          weekIndex,
          label: date.year() !== currentYear ? date.format('MMM YY') : date.format('MMM'),
        }));
    },

    appendDayLabel(parent, { x, y, text }) {
      parent.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '9px')
        .attr('fill', 'var(--text-tertiary, #768390)')
        .text(text);
    },

    appendMonthLabel(parent, { x, y, text }) {
      parent.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('font-size', '10px')
        .attr('fill', 'var(--text-secondary, #768390)')
        .text(text);
    },

    render2dGrid(svg, weeks) {
      const {
        cellSize, cellGap, margin, dayLabelOffset, monthLabelOffset,
      } = HEATMAP_GRID_LAYOUT;

      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Create color scale (GitHub-style)
      const colorScale = this.createColorScale();

      // Render cells
      weeks.forEach((week, weekIndex) => {
        week.forEach(day => {
          const x = weekIndex * (cellSize + cellGap);
          const y = day.dayOfWeek * (cellSize + cellGap);

          g.append('rect')
            .attr('x', x)
            .attr('y', y)
            .attr('width', cellSize)
            .attr('height', cellSize)
            .attr('fill', colorScale(day.count))
            .attr('stroke', 'none')
            .attr('rx', 2)
            .style('cursor', day.count > 0 ? 'pointer' : 'default')
            .on('mouseover', event => this.handleCellHover(event, day))
            .on('mouseout', () => this.hideTooltip())
            .on('click', event => this.handleCellClick(event, day));
        });
      });

      // Add day-of-week labels (Mon, Wed, Fri)
      HEATMAP_DAY_LABELS.forEach(({ day, label }) => {
        this.appendDayLabel(g, {
          x: -dayLabelOffset,
          y: day * (cellSize + cellGap) + cellSize / 2,
          text: label,
        });
      });

      // Add month labels
      this.getMonthLabels(weeks).forEach(({ weekIndex, label }) => {
        this.appendMonthLabel(g, {
          x: weekIndex * (cellSize + cellGap),
          y: -monthLabelOffset,
          text: label,
        });
      });
    },

    render3dGrid(svg, weeks, size) {
      const { daysInWeek } = HEATMAP_GRID_LAYOUT;
      const { maxBarHeight, padding, faceSeamStrokeWidth } = HEATMAP_3D_SETTINGS;

      const project = createProjection3d(HEATMAP_3D_SETTINGS);
      // Fit the tallest possible chart, so the cell size doesn't change with the data
      const chartCorners = getBoxCorners({ width: weeks.length, depth: daysInWeek, height: maxBarHeight });
      const fitToSvg = createFitTransform(getBounds(chartCorners.map(project)), {
        x: padding.left,
        y: padding.top,
        width: size.width - padding.left - padding.right,
        height: size.height - padding.top - padding.bottom,
      });
      const toScreen = point => fitToSvg(project(point));
      const shouldAnimate = !this.prefersReducedMotion();

      const bars = svg.append('g')
        .selectAll('g')
        .data(this.build3dBars(weeks))
        .join('g')
        .style('cursor', bar => (bar.day.count > 0 ? 'pointer' : 'default'))
        .on('mouseenter', (event, bar) => this.handle3dBarHover(event, bar))
        .on('mouseleave', event => this.handle3dBarLeave(event))
        .on('click', (event, bar) => this.handleCellClick(event, bar.day));

      const faces = bars.selectAll('polygon')
        .data(bar => bar.faces)
        .join('polygon')
        .attr('points', face => this.get3dFacePoints(face, shouldAnimate ? face.startHeight : face.height, toScreen))
        .style('fill', face => face.fill)
        .style('stroke', face => face.fill)
        .style('stroke-width', faceSeamStrokeWidth)
        .style('stroke-linejoin', 'round');

      if (shouldAnimate) this.grow3dBars(faces, toScreen);
      this.render3dLabels(svg, weeks, { project, toScreen });
    },

    render3dLabels(svg, weeks, { project, toScreen }) {
      const { daysInWeek } = HEATMAP_GRID_LAYOUT;
      const { dayLabelOffset, monthLabelOffset } = HEATMAP_3D_SETTINGS;
      // Labels lie on the floor, so they must never steal hover from the bars
      const labels = svg.append('g').style('pointer-events', 'none');

      HEATMAP_DAY_LABELS.forEach(({ day, label }) => {
        this.appendGroundLabel(labels, {
          transform: getGroundTextTransform(project, toScreen([0, day + 0.5, 0])),
          x: -dayLabelOffset,
          y: 0,
          textAnchor: 'end',
          baseline: 'middle',
          fill: 'var(--text-tertiary, #768390)',
          text: label,
        });
      });

      // Month labels lie in front of the grid's front edge, where bars can't cover them
      this.getMonthLabels(weeks).forEach(({ weekIndex, label }) => {
        this.appendGroundLabel(labels, {
          transform: getGroundTextTransform(project, toScreen([weekIndex, daysInWeek, 0])),
          x: 0,
          y: monthLabelOffset,
          textAnchor: 'start',
          baseline: 'hanging',
          fill: 'var(--text-secondary, #768390)',
          text: label,
        });
      });
    },

    appendGroundLabel(parent, {
      transform, x, y, textAnchor, baseline, fill, text,
    }) {
      parent.append('text')
        .attr('transform', transform)
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', textAnchor)
        .attr('dominant-baseline', baseline)
        .attr('font-size', `${HEATMAP_3D_SETTINGS.labelFontSize}px`)
        .attr('fill', fill)
        .text(text);
    },

    build3dBars(weeks) {
      const {
        maxBarHeight, minBarHeight, emptyCellHeight, cellGap, faceBrightness, emptyFaceColors, hoverHighlight,
      } = HEATMAP_3D_SETTINGS;
      const colorScale = this.createColorScale();
      const maxCount = d3.max(weeks, week => d3.max(week, day => day.count)) || 1;
      const inset = cellGap / 2;
      const highlight = color => d3.interpolateRgb(color, hoverHighlight.color)(hoverHighlight.ratio);

      return weeks
        .flatMap((week, weekIndex) => week.map(day => ({ weekIndex, day })))
        // Painter's algorithm: cells further from the camera are drawn first so nearer bars cover them
        .sort((first, second) => (first.weekIndex + first.day.dayOfWeek) - (second.weekIndex + second.day.dayOfWeek))
        .map(({ weekIndex, day }) => {
          const hasScans = day.count > 0;
          const height = hasScans
            ? minBarHeight + (day.count / maxCount) * (maxBarHeight - minBarHeight)
            : emptyCellHeight;
          const footprint = {
            x0: weekIndex + inset,
            x1: weekIndex + 1 - inset,
            y0: day.dayOfWeek + inset,
            y1: day.dayOfWeek + 1 - inset,
          };

          const faces = Object.keys(faceBrightness).map(faceName => {
            // Empty slabs use themed CSS variables, which d3.color can't shade, so they aren't highlighted either
            const fill = hasScans ? this.shadeColor(colorScale(day.count), faceBrightness[faceName]) : emptyFaceColors[faceName];
            return {
              faceName,
              footprint,
              height,
              startHeight: emptyCellHeight,
              weekIndex,
              fill,
              highlightFill: hasScans ? highlight(fill) : fill,
            };
          });

          return { day, faces };
        });
    },

    get3dFacePoints(face, height, toScreen) {
      const corners = getBarFaces({ ...face.footprint, height })[face.faceName];
      return toSvgPoints(corners.map(toScreen));
    },

    // Bars rise from the empty slab height, sweeping left to right like the reference contribution chart
    grow3dBars(faces, toScreen) {
      const { durationMs, weekStaggerMs } = HEATMAP_3D_SETTINGS.growAnimation;

      faces
        .filter(face => face.height > face.startHeight)
        .transition()
        .duration(durationMs)
        .delay(face => face.weekIndex * weekStaggerMs)
        .ease(d3.easeCubicOut)
        .attrTween('points', face => {
          const heightAt = d3.interpolateNumber(face.startHeight, face.height);
          return progress => this.get3dFacePoints(face, heightAt(progress), toScreen);
        });
    },

    shadeColor(color, brightness) {
      const { r, g, b } = d3.rgb(color);
      return d3.rgb(r * brightness, g * brightness, b * brightness).formatHex();
    },

    prefersReducedMotion() {
      return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    },

    handle3dBarHover(event, bar) {
      this.setBarHighlight(event.currentTarget, true);
      this.handleCellHover(event, bar.day);
    },

    handle3dBarLeave(event) {
      this.setBarHighlight(event.currentTarget, false);
      this.hideTooltip();
    },

    setBarHighlight(barElement, isHighlighted) {
      d3.select(barElement)
        .selectAll('polygon')
        .style('fill', face => (isHighlighted ? face.highlightFill : face.fill))
        .style('stroke', face => (isHighlighted ? face.highlightFill : face.fill));
    },

    createColorScale() {
      return count => {
        if (count === 0) return 'var(--heatmap-empty, rgba(128, 128, 128, 0.15))';
        if (count <= 2) return '#40c463';
        if (count <= 5) return '#30a14e';
        if (count <= 10) return '#216e39';
        return '#0e4429';
      };
    },

    calculateCellSize() {
      // Calculate cell size based on container dimensions
      const margin = {
        top: 30, right: 20, bottom: 10, left: 50,
      };
      const availableWidth = this.width - margin.left - margin.right;
      const availableHeight = this.height - margin.top - margin.bottom;

      // 7 rows for days of week
      const cellHeight = Math.floor((availableHeight - 6 * 2) / 7); // 2px gap between cells

      // Calculate weeks needed
      const startDate = moment(this.dateRange.start);
      const endDate = moment(this.dateRange.end);
      const weeks = Math.ceil(endDate.diff(startDate, 'days') / 7);

      const cellWidth = Math.floor((availableWidth - (weeks - 1) * 2) / weeks);

      return {
        width: Math.max(10, Math.min(cellWidth, 15)),
        height: Math.max(10, Math.min(cellHeight, 15)),
      };
    },

    handleCellHover(event, data) {
      this.showTooltip(event, data);
    },

    showTooltip(event, data) {
      const scanLabel = data.count === 1
        ? this.$t('dashboard.scans.details.scan')
        : this.$t('dashboard.scans.details.scans');
      this.tooltip.content = data.count > 0
        ? `${data.count} ${scanLabel} on ${this.formatDate(data.date)}`
        : `${this.$t('dashboard.scans.tooltip.no.scans').replace('{date}', this.formatDate(data.date))}`;
      this.tooltip.x = event.pageX + 10;
      this.tooltip.y = event.pageY - 30;
      this.tooltip.visible = true;
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },

    handleCellClick(event, data) {
      if (data.count === 0) return;

      this.openScanDetailsModal(data);
    },

    openScanDetailsModal(data) {
      this.modal.date = this.formatDate(data.date);
      this.modal.scans = data.scans;
      this.modal.totalCount = data.count;
      this.modal.hoveredType = null;
      this.modal.groupedScans = this.groupScansByType(data.scans);
      this.modal.visible = true;

      this.$nextTick(() => {
        this.renderLoadVolumeChart(data.scans);
      });
    },

    closeModal() {
      this.modal.visible = false;
      this.modal.hoveredType = null;
    },

    groupScansByType(scans) {
      const groups = {};
      scans.forEach(scan => {
        if (!groups[scan.scanType]) {
          groups[scan.scanType] = { scanType: scan.scanType, count: 0, scans: [] };
        }
        groups[scan.scanType].count += 1;
        groups[scan.scanType].scans.push(scan);
      });
      return Object.values(groups).sort((a, b) => b.count - a.count);
    },

    getUrlsForType(scanType) {
      const group = this.modal.groupedScans.find(g => g.scanType === scanType);
      if (!group) return [];
      const urls = [...new Set(group.scans.map(s => s.url || s.tld).filter(Boolean))];
      return urls;
    },

    renderLoadVolumeChart(scans) {
      const container = this.$refs.loadVolumeChart;
      if (!container) return;

      d3.select(container).selectAll('*').remove();

      // Group scans by hour
      const hourCounts = new Array(24).fill(0);
      scans.forEach(scan => {
        if (scan.time) {
          const hour = parseInt(scan.time.split(':')[0], 10);
          if (hour >= 0 && hour < 24) {
            hourCounts[hour] += 1;
          }
        }
      });

      const chartWidth = 660;
      const chartHeight = 80;
      const margin = {
        top: 10, right: 15, bottom: 20, left: 30,
      };
      const width = chartWidth - margin.left - margin.right;
      const height = chartHeight - margin.top - margin.bottom;

      const svg = d3.select(container)
        .append('svg')
        .attr('width', chartWidth)
        .attr('height', chartHeight);

      // Gradient definition
      const defs = svg.append('defs');
      const gradient = defs.append('linearGradient')
        .attr('id', 'load-volume-gradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '0%');
      gradient.append('stop').attr('offset', '0%').attr('stop-color', '#00bcd4');
      gradient.append('stop').attr('offset', '100%').attr('stop-color', '#e040fb');

      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleLinear().domain([0, 23]).range([0, width]);
      const y = d3.scaleLinear()
        .domain([0, Math.max(d3.max(hourCounts), 1)])
        .range([height, 0]);

      // Area generator with smooth curve
      const area = d3.area()
        .x((d, i) => x(i))
        .y0(height)
        .y1(d => y(d))
        .curve(d3.curveBasis);

      // Line generator
      const line = d3.line()
        .x((d, i) => x(i))
        .y(d => y(d))
        .curve(d3.curveBasis);

      // Draw area fill
      g.append('path')
        .datum(hourCounts)
        .attr('fill', 'url(#load-volume-gradient)')
        .attr('fill-opacity', 0.3)
        .attr('d', area);

      // Draw line
      g.append('path')
        .datum(hourCounts)
        .attr('fill', 'none')
        .attr('stroke', 'url(#load-volume-gradient)')
        .attr('stroke-width', 2)
        .attr('d', line);

      // X axis
      g.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(
          d3.axisBottom(x)
            .tickValues([0, 6, 12, 18, 23])
            .tickFormat(d => `${d}:00`),
        )
        .selectAll('text')
        .attr('fill', 'var(--text-secondary, #768390)')
        .attr('font-size', '9px');

      // Style axis lines
      g.selectAll('.domain, .tick line')
        .attr('stroke', 'var(--border-secondary, rgba(128,128,128,0.2))');
    },

    formatDate(dateStr) {
      return moment(dateStr).format('DD MMM YYYY');
    },

    formatScanType(scanType) {
      // Convert enum value to readable format
      const typeMap = {
        WEB_VULNERABILITY_ASSESSMENT: 'Web Vulnerability Assessment',
        NETWORK_VULNERABILITY_ASSESSMENT: 'Network Vulnerability Assessment',
        PHISHING_SIMULATION: 'Phishing Simulation',
        BRAND_PROTECTION: 'Brand Protection',
        DNSTWIST: 'DNS Twist',
        // Add more mappings as needed
      };

      return typeMap[scanType] || scanType.replace(/_/g, ' ');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.scans-heatmap-widget {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  position: relative;

  .error-message {
    color: var(--text-error, #f44336);
    font-family: $font-family-regular;
    font-size: $font-size-md;
    text-align: center;
    padding: rem(20);
  }

  .heatmap-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow-x: auto;
    overflow-y: hidden;
  }
}

.heatmap-tooltip {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-family: $font-family-regular;
  pointer-events: none;
  z-index: 1000;
  white-space: nowrap;
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.scan-details-popup {
  padding: $space-8 $space-10;
  background-color: var(--bg-primary) !important;

  &__action {
    .close_btn {
      margin-right: rem(-20);
      margin-top: rem(-12);
      float: right;

      &:hover {
        cursor: pointer;
        color: var(--brand-primary);
      }
    }
  }

  &__title {
    color: var(--text-primary);
    font-size: $font-size-xl;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: rem(24);
    text-transform: uppercase;
    padding-bottom: $space-2;
  }

  &__subtitle {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    padding-bottom: rem(20);
  }

  &__body {
    display: flex;
    gap: $space-6;
    min-height: rem(120);
  }

  &__separator {
    width: 1px;
    background-color: var(--border-secondary, rgba(128, 128, 128, 0.2));
    align-self: stretch;
  }

  &__chart {
    margin-top: rem(20);
    padding-top: rem(16);
    border-top: 1px solid var(--border-secondary, rgba(128, 128, 128, 0.2));
  }

  &__chart-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-bottom: $space-2;
  }

  &__buttons {
    text-align: right;
    padding-top: rem(20);

    .ok-button.v-btn {
      height: $space-8;
      min-width: rem(51);
      border-radius: $radius-sm;
      background-color: var(--brand-primary) !important;
      box-shadow: $shadow-sm;
      color: var(--text-inverse) !important;
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $space-4;
      text-align: center;
    }
  }
}

.scan-type-cards {
  display: flex;
  flex-wrap: wrap;
  gap: $space-3;
  flex: 2;
}

.scan-type-card {
  display: flex;
  align-items: center;
  gap: $space-4;
  padding: $space-4 $space-5;
  border-radius: $radius-md;
  background-color: var(--bg-primary);
  cursor: pointer;
  width: calc(33.33% - #{$space-2});

  &__info {
    flex: 1;
  }

  &__name {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    text-transform: capitalize;
    margin-bottom: $space-1;
  }

  &__count {
    color: var(--text-primary);
    font-size: rem(28);
    font-weight: 600;
    line-height: 1.2;
  }
}

.scan-urls-panel {
  flex: 1;
  max-width: rem(280);
  padding: $space-3 $space-4;
  border-radius: $radius-md;
  background-color: var(--bg-primary);
  overflow-y: auto;
  max-height: rem(200);

  &__title {
    color: var(--text-secondary);
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-bottom: $space-2;
  }

  &__item {
    display: flex;
    align-items: center;
    color: var(--text-primary);
    font-size: $font-size-sm;
    padding: $space-1 0;
    word-break: break-all;
  }

  &__empty {
    color: var(--text-tertiary, #768390);
    font-size: $font-size-xs;
    font-style: italic;
  }
}

.load-volume-container {
  display: flex;
  justify-content: center;
}
</style>
